import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function EventStaffAssignment({ open, onClose, event, token }) {
    const [employees, setEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [assignedStaff, setAssignedStaff] = useState([]);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (open && event) {
            fetchEmployees();
            fetchAssignedStaff();
        }
    }, [open, event]);

    const fetchEmployees = async () => {
        try {
            const response = await fetch(`${API_URL}/api/employees`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                // Фильтруем только активных сотрудников
                setEmployees(data.employees.filter(e => e.status === 'active'));
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const fetchAssignedStaff = async () => {
        try {
            const response = await fetch(
                `${API_URL}/api/events/${event.id}/staff`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            const data = await response.json();
            if (data.success) {
                setAssignedStaff(data.staff);
            }
        } catch (error) {
            console.error('Error fetching assigned staff:', error);
        }
    };

    const handleAssign = async () => {
        if (selectedEmployees.length === 0) {
            setError('Выберите хотя бы одного сотрудника');
            return;
        }

        setError('');
        setSuccess('');

        try {
            const response = await fetch(
                `${API_URL}/api/events/${event.id}/staff`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        employeeIds: selectedEmployees.map(e => e.id)
                    })
                }
            );

            const data = await response.json();

            if (data.success) {
                setSuccess('Сотрудники назначены');
                setSelectedEmployees([]);
                fetchAssignedStaff();
            } else {
                setError(data.error || 'Ошибка при назначении сотрудников');
            }
        } catch (error) {
            setError('Ошибка подключения к серверу');
        }
    };

    const handleRemove = async (employeeId) => {
        try {
            const response = await fetch(
                `${API_URL}/api/events/${event.id}/staff/${employeeId}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            const data = await response.json();

            if (data.success) {
                setSuccess('Сотрудник удален из списка');
                fetchAssignedStaff();
            } else {
                setError(data.error || 'Ошибка при удалении');
            }
        } catch (error) {
            setError('Ошибка подключения к серверу');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center" gap={1}>
                    <PersonAddIcon color="primary" />
                    <Typography variant="h6">
                        Назначить сотрудников: {event?.title}
                    </Typography>
                </Box>
            </DialogTitle>

            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Дата мероприятия: {event && new Date(event.event_date).toLocaleDateString('ru-RU')}
                </Typography>

                {/* Список уже назначенных сотрудников */}
                <Box mb={3}>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                        Назначенные сотрудники ({assignedStaff.length})
                    </Typography>
                    {assignedStaff.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                            Сотрудники пока не назначены
                        </Typography>
                    ) : (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                            {assignedStaff.map((staff) => (
                                <Chip
                                    key={staff.employee_id}
                                    label={`${staff.full_name} (${staff.position})`}
                                    onDelete={() => handleRemove(staff.employee_id)}
                                    color="primary"
                                />
                            ))}
                        </Box>
                    )}
                </Box>

                {/* Добавление новых сотрудников */}
                <Box>
                    <Typography variant="subtitle1" fontWeight="bold" mb={1}>
                        Добавить сотрудников
                    </Typography>
                    <Autocomplete
                        multiple
                        options={employees.filter(
                            e => !assignedStaff.some(s => s.employee_id === e.id)
                        )}
                        getOptionLabel={(option) => `${option.full_name} (${option.position})`}
                        value={selectedEmployees}
                        onChange={(event, newValue) => {
                            setSelectedEmployees(newValue);
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Выберите сотрудников"
                                placeholder="Начните вводить имя..."
                            />
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip
                                    label={option.full_name}
                                    {...getTagProps({ index })}
                                />
                            ))
                        }
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Закрыть</Button>
                {selectedEmployees.length > 0 && (
                    <Button
                        onClick={handleAssign}
                        variant="contained"
                        startIcon={<PersonAddIcon />}
                    >
                        Назначить ({selectedEmployees.length})
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}

export default EventStaffAssignment;
